import { type ReactNode, useState } from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/base/dialog';
import { Alert, AlertVariant } from '@ui/components/feedback/Alert';

enum ConfirmDialogVariant {
  DEFAULT = 'default',
  DESTRUCTIVE = 'destructive',
}

const CONFIRM_BUTTON_VARIANTS: Record<ConfirmDialogVariant, ButtonVariant> = {
  [ConfirmDialogVariant.DEFAULT]: ButtonVariant.DEFAULT,
  [ConfirmDialogVariant.DESTRUCTIVE]: ButtonVariant.DESTRUCTIVE,
};

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  mainText: string;
  warning?: string;
  confirmText: string;
  cancelText?: string;
  confirmAction: () => Promise<void> | void;
  cancelAction: () => void;
  variant?: ConfirmDialogVariant;
  zIndex?: number;
  children?: ReactNode;
}

const ConfirmDialog = ({
  open,
  title,
  mainText,
  warning,
  confirmText,
  cancelText = 'Cancel',
  confirmAction,
  cancelAction,
  variant = ConfirmDialogVariant.DEFAULT,
  zIndex,
  children,
}: ConfirmDialogProps) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await confirmAction();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) cancelAction();
      }}
    >
      <DialogContent showCloseButton={false} zIndex={zIndex}>
        <DialogHeader>
          <DialogTitle>{title ?? 'Confirm'}</DialogTitle>
        </DialogHeader>
        {warning && <Alert variant={AlertVariant.WARNING}>{warning}</Alert>}
        <p className="text-sm text-foreground">{mainText}</p>
        {children}
        <DialogFooter>
          <Button variant={ButtonVariant.OUTLINE} onClick={cancelAction} disabled={loading}>
            {cancelText}
          </Button>
          <Button variant={CONFIRM_BUTTON_VARIANTS[variant]} onClick={handleConfirm} disabled={loading}>
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ConfirmDialog, ConfirmDialogVariant };
